<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('Content-Module') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Bibliothek anzeigen')" :aria-label="$t('Bibliothek anzeigen')" to="/workspace/cms/module-library">
                        <svg-vue class="h-4 w-4" icon="regular.text"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Preset anlegen')" :aria-label="$t('Preset anlegen')" to="/workspace/cms/content-modules/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div v-if="moduleTypes.length > 0" class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Content-Modul') }}</th>
                                        <th class="table-listview-l-th">{{ $t('Beschreibung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-24 text-center">{{ $t('Info') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Letzte Veröffentlichung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Update') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="moduleType in moduleTypes"
                                        :key="moduleType.id"
                                        class="cursor-pointer"
                                        @click="$router.push('/workspace/cms/content-modules/' + moduleType.id + '/edit')"
                                    >
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0">
                                                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" icon="font-awesome.regular.cube"></svg-vue>
                                                </div>
                                                <div class="min-w-0">
                                                    <div class="flex items-center gap-2 min-w-0">
                                                        <div class="text-sm font-medium text-gray-900 truncate">{{ moduleType.name }}</div>
                                                        <span :class="moduleTypeStatusMeta(moduleType).dotClass" class="h-2 w-2 rounded-full flex-shrink-0"></span>
                                                    </div>
                                                    <div class="text-xs text-gray-400 mt-1">{{ moduleType.key }}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td text-sm text-gray-500">
                                            <span class="block max-w-full truncate">{{ moduleType.description || '-' }}</span>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-center">
                                            <div class="flex items-center justify-center w-full">
                                                <app-tooltip
                                                    placement="bottom"
                                                    width-class="w-max max-w-xs"
                                                    class="inline-flex items-center justify-center"
                                                >
                                                    <svg-vue class="h-4 w-4 text-gray-500" icon="font-awesome.regular.circle-info"></svg-vue>
                                                    <template #content>
                                                        <div class="inline-flex flex-col text-left">
                                                            <div
                                                                v-for="(line, lineIndex) in infoTooltipLines(moduleType)"
                                                                :key="'cms-module-type-info-' + moduleType.id + '-' + lineIndex"
                                                            >
                                                                {{ line }}
                                                            </div>
                                                        </div>
                                                    </template>
                                                </app-tooltip>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="moduleType.published_by_user" class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    :src="publishedActorAvatar(moduleType)"
                                                    :alt="publishedActorName(moduleType)"
                                                    :title="publishedActorName(moduleType)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ publishedActorName(moduleType) }}</span>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-500">
                                            {{ moduleType.updated_at ? $momentFormatDateTimeAgo(moduleType.updated_at) : '-' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="h-full">
                            <empty-state />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    cmsPublishedActorAvatar,
    cmsPublishedActorName,
} from '@/utils/cms-update-meta';
import {cmsPublicationStatusMeta} from '@/utils/cms-status-badge';

export default {
    name: "cms-module-types-list",
    data() {
        return {
            loading: true,
            moduleTypes: [],
        };
    },
    mounted() {
        this.loadModuleTypes();
    },
    methods: {
        publishedActorAvatar(moduleType) {
            return cmsPublishedActorAvatar(moduleType);
        },
        publishedActorName(moduleType) {
            return cmsPublishedActorName(moduleType);
        },
        moduleTypeStatusMeta(moduleType) {
            return cmsPublicationStatusMeta(this, moduleType);
        },
        infoTooltipLines(moduleType) {
            return [
                `${this.$t('Felder')}: ${moduleType.fields_count}`,
                `${this.$t('Slots')}: ${moduleType.slots_count}`,
                `${this.$t('Module')}: ${moduleType.modules_count}`,
            ];
        },
        presetIcon(icon) {
            return 'font-awesome.regular.cube';
        },
        loadModuleTypes() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/module-types').then((response) => {
                const rows = Array.isArray(response.data) ? response.data : [];
                this.moduleTypes = rows.map((row) => ({
                    ...row,
                    fields_count: Number((row.settings_schema || []).length) + Number((row.translation_schema || []).length) || row.fields_count || 0,
                    slots_count: row.slots_count || 0,
                }));
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
    },
}
</script>
